import React, { useEffect, useState } from 'react'
import Loading from 'atoms/Loading'
import { toast } from 'atoms/Toast'
import LSDate, { DATE_FORMAT_MMDDYYYYHHMMSS_AMPM } from 'common/Date.helpers'
import { MessageRead, MessageLogRead } from 'common/types/kraken-core/Message'
import { readMessageLogs } from 'services/kraken-core/messages/messages.service'
import useCancelToken from 'hooks/useCancelToken'
import { Tag, TagProps } from '@loadsmart/loadsmart-ui'
import { Themes } from '@loadsmart/loadsmart-ui/dist/theming'
import {
  TableCell,
  TableContainer,
  TableHead,
  Table,
  TableRow,
  Typography,
  Chip,
  Box,
  Stack,
  Tooltip,
} from '@mui/material'
import styled from 'styled-components'
import isEmpty from 'lodash.isempty'
import analytics, { AnalyticsEvent } from 'common/analytics'
import { ROUTES } from 'routing/routes'
import mapping from 'atoms/Icon/mapping'
import SyntaxHighlighter from 'react-syntax-highlighter'
import { Error as ErrorIcon, Warning } from '@mui/icons-material'

export interface MessageLogsProps {
  message: MessageRead
}

interface StyledFilterTagProps extends TagProps {
  selected: boolean
}

const TAG_ALL = 'ALL'

const StyledTag = (props: StyledFilterTagProps) => <Tag {...props}>{props.children}</Tag>

const StyledFilterTag = styled(StyledTag)`
  border: ${props => (props.selected ? '3px solid' : '')};
`

const SYNTAX_HIGHLIGHT_LANGUAGE = 'python'
const SYNTAX_HIGHLIGHT_STYLE = {
  background: 'transparent',
}

// LogRenderer interface and registry
interface ILogRenderer {
  accept: RegExp
  symbol?: React.ReactNode
  render: (log: MessageLogRead) => React.ReactNode
}

const getLogLevelStyle = (level: string) => {
  const mapping: any = {
    ERROR: {
      style: {
        backgroundColor: Themes.Loadsmart['banner-background-danger'],
      },
      symbol: (
        <Tooltip title="Error">
          <ErrorIcon />
        </Tooltip>
      ),
    },
    WARNING: {
      style: {
        backgroundColor: Themes.Loadsmart['color-warning-light'],
      },
      symbol: (
        <Tooltip title="Warning">
          <Warning />
        </Tooltip>
      ),
    },
  }
  return mapping[level] || ''
}

const TranslationMapLogRenderer: ILogRenderer = {
  accept: /^Translating message using map: .*$/, // Accepts TranslationMap logs (dotAll alternative)
  symbol: <Tooltip title="Translation">{mapping['TranslationMap']}</Tooltip>,
  render: log => {
    // Extract the map name from the log
    const match = log.log.match(/^(Translating message using map:)\s+([a-f0-9-]{36})\s+-\s+(.+)$/)
    if (!match || match.length < 3) {
      throw new Error('Invalid log format for TranslationMapLogRenderer')
    }
    const logMessage = match[1]
    const mapId = match[2]
    const mapName = match[3]

    return (
      <Box display="flex" alignItems="center" gap={1}>
        <SyntaxHighlighter
          language={SYNTAX_HIGHLIGHT_LANGUAGE}
          wrapLongLines={true}
          customStyle={SYNTAX_HIGHLIGHT_STYLE}
        >
          {logMessage}{' '}
        </SyntaxHighlighter>
        <Chip
          label={
            <Box component="span" fontWeight="bold">
              {mapName ?? mapId}
            </Box>
          }
          size="small"
          color="primary"
          onClick={() => {
            window.open(`${ROUTES.TRANSLATION_MAPS}/${mapId}`, '_blank')
          }}
        />
      </Box>
    )
  },
}

const RoutingStepLogRenderer: ILogRenderer = {
  accept: /^Running step .*$/,
  symbol: <Tooltip title="Routing">{mapping['Route']}</Tooltip>,
  render: log => {
    const match = log.log.match(/^(Running step) (.*) with processors (.*$)/)
    if (!match || match.length < 2) {
      throw new Error('Invalid log format for RoutingStepLogRenderer')
    }

    const preFix = match[1] // "Running step"
    const step = match[2] // The step name
    const processors = match[3] || '' // The processors, if any

    return (
      <span>
        <Typography
          component="span"
          variant="h4"
          fontFamily={'monospace'}
          fontSize={'0.9rem'}
          marginLeft={1}
        >
          {preFix}{' '}
        </Typography>
        <Typography component="span" variant="h4" fontWeight="bold">
          {step}
        </Typography>
        <br />
        <SyntaxHighlighter
          language={SYNTAX_HIGHLIGHT_LANGUAGE}
          wrapLongLines={true}
          customStyle={SYNTAX_HIGHLIGHT_STYLE}
        >
          {processors}
        </SyntaxHighlighter>
      </span>
    )
  },
}

const ConnectorSendOutLogRenderer: ILogRenderer = {
  accept: /^Message.* sent with Connector.*$/,
  symbol: <Tooltip title="Connector Send Out">{mapping['ConnectionSettings']}</Tooltip>,
  render: log => {
    return DefaultLogRenderer.render(log)
  },
}

const DefaultLogRenderer: ILogRenderer = {
  accept: /[\s\S]*/,
  symbol: null,
  render: log => (
    <SyntaxHighlighter
      language={SYNTAX_HIGHLIGHT_LANGUAGE}
      wrapLongLines={true}
      customStyle={SYNTAX_HIGHLIGHT_STYLE}
    >
      {log.log}
    </SyntaxHighlighter>
  ),
}

const logRenderers: ILogRenderer[] = [
  ConnectorSendOutLogRenderer,
  TranslationMapLogRenderer,
  RoutingStepLogRenderer,
  DefaultLogRenderer,
]

function TableRowLogRenderer({ log }: { log: MessageLogRead }) {
  const renderer = logRenderers.find(r => r.accept.test(log.log)) || DefaultLogRenderer
  const createdAt = LSDate(log.created_at)?.format(DATE_FORMAT_MMDDYYYYHHMMSS_AMPM)
  const levelStyle = getLogLevelStyle(log.log_level)
  let renderedLog: React.ReactNode
  try {
    renderedLog = renderer.render(log)
  } catch (error) {
    renderedLog = DefaultLogRenderer.render(log)
  }

  return (
    <TableRow key={`${log.created_at}-${log.log}`} style={levelStyle.style}>
      <TableCell>
        <Typography variant="h5">{createdAt}</Typography>
      </TableCell>
      <TableCell size="small" width={1}>
        {levelStyle.symbol ?? renderer.symbol}
      </TableCell>
      <TableCell>{renderedLog}</TableCell>
    </TableRow>
  )
}

const MessageLogsView = ({ message }: MessageLogsProps) => {
  const [logTags, setLogTags] = useState<Array<React.ReactElement>>([])
  const [filteredLogTags, setFilteredLogTags] = useState<Array<string>>([TAG_ALL])
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [logs, setLogs] = useState<MessageLogRead[]>([])

  const { getSource, clearSource, cancelPending, isCancel } = useCancelToken()

  const onClikTagLevelFilter = (level: string) => {
    const selected = filteredLogTags.filter(t => t === level).length > 0
    analytics.event({
      action: 'click',
      category: AnalyticsEvent.MessageDetailLogFilter,
      value: level,
    })

    if (level === TAG_ALL) {
      setFilteredLogTags(c => [level])
    } else {
      if (selected) {
        setFilteredLogTags(c => c.filter(v => v != level))
      } else {
        const tagsExceptAll = filteredLogTags.filter(v => v != TAG_ALL)
        setFilteredLogTags(c => [...tagsExceptAll, level])
      }
    }
  }

  useEffect(() => {
    const source = getSource()

    ;(async () => {
      setIsLoading(true)
      const [error, response] = await readMessageLogs(message.id, {
        cancelToken: source.token,
      })

      clearSource()
      setIsLoading(false)

      if (error && !isCancel) return toast.error(`Something went wrong: ${error}`)

      return setLogs(response?.data || [])
    })()

    return () => cancelPending()
  }, [message.id])

  useEffect(
    function updateLogFilterTags() {
      const tags = [
        <StyledFilterTag
          key={'tag_all'}
          className="cursor-pointer"
          onClick={() => onClikTagLevelFilter(TAG_ALL)}
          selected={filteredLogTags.filter(t => t === TAG_ALL).length > 0}
        >
          ALL
        </StyledFilterTag>,
      ]
      const levels = new Set(logs.filter(c => !isEmpty(c.log_level)).map(c => c.log_level))
      levels.forEach(level => {
        const upperCaseLevel = level.toUpperCase()
        const style = getLogLevelStyle(level).style
        tags.push(
          <StyledFilterTag
            key={`tag_${level}`}
            className="cursor-pointer"
            style={style}
            onClick={() => onClikTagLevelFilter(level)}
            selected={filteredLogTags.filter(t => t === upperCaseLevel).length > 0}
          >
            {upperCaseLevel}
          </StyledFilterTag>
        )
      })
      setLogTags(
        tags.sort((a, b) => {
          if (a.key && b.key) {
            return a.key.toLocaleString().localeCompare(b.key.toString())
          }
          return 0
        })
      )
    },
    [logs, filteredLogTags]
  )

  useEffect(() => {
    if (filteredLogTags.length === 0) {
      setFilteredLogTags([TAG_ALL])
    }
  }, [filteredLogTags])

  return (
    <Box>
      {isLoading && <Loading className="justify-center mt-8" />}

      {!isLoading && (
        <Stack>
          <Box
            data-testid="message-logs-filterable-tags"
            gap={1}
            display="flex"
            flexWrap="wrap"
            padding={2}
          >
            {logTags.map(logTag => {
              return logTag
            })}
          </Box>
          <TableContainer data-testid="message-logs-list">
            <Table size="small" padding="normal" data-testid="message-logs-list-body">
              <TableHead>
                <TableCell>Created At</TableCell>
                <TableCell width={1} />
                <TableCell>Log</TableCell>
              </TableHead>
              {logs
                ?.filter(log => {
                  if (filteredLogTags.find(l => l === TAG_ALL)) return log
                  return filteredLogTags.find(l => l === log.log_level)
                })
                .map((log, index) => {
                  return <TableRowLogRenderer key={index} log={log} />
                })}
            </Table>
          </TableContainer>
        </Stack>
      )}
    </Box>
  )
}

export default MessageLogsView
